"use client";

import { Suspense, useMemo } from "react";
import {
	RecommendationsCarousel,
	type RecommendedProduct,
} from "@/components/ui/sections/RecommendationsCarousel";
import { useTranslations } from "@/i18n/client";
import { useRecommendations } from "@/lib/depict/useRecommendations";
import { useCart } from "./hooks";

const UPSELL_LIMIT = 8;

/**
 * Renders Depict's `after_basket` recommendations under the cart line items.
 * Quietly hides itself if the cart is empty, Depict returns nothing, or the
 * request fails — the cart should still feel finished without it.
 *
 * Tapping a card's [+ ADD] reveals an inline `QuickAddPanel` so customers can
 * pick colour + size without leaving the drawer; the actual add-to-cart goes
 * through the shared buy-box flow on PDP.
 */
export const CartUpsell = () => {
	return (
		<Suspense fallback={null}>
			<CartUpsellInner />
		</Suspense>
	);
};

const CartUpsellInner = () => {
	const t = useTranslations();
	const { data: cart } = useCart();

	// Depict's `after_basket` is keyed on the items currently in the cart.
	// We send Centra item SKUs because that's how ellaandil's catalogue is
	// ingested into Depict (see lib/depict/product-id.ts) and what the PDP
	// tracking flow uses for variant attribution.
	const productIds = useMemo(
		() => (cart?.items ?? []).map((item) => item?.sku).filter((id): id is string => Boolean(id)),
		[cart?.items],
	);

	const { data } = useRecommendations({
		productIds,
		type: "after_basket",
		limit: UPSELL_LIMIT,
		enabled: productIds.length > 0,
	});

	const products = (data?.products ?? []) as RecommendedProduct[];

	return (
		<RecommendationsCarousel
			products={products}
			heading={t("You may also like")}
			showSwatches={false}
			clampName
		/>
	);
};
